/**
 * エラーダイアログ表示のためのイベントシステム
 * 循環参照を回避しつつ、queryClientからダイアログ表示を可能にする
 */

import { ERROR_EVENTS, NAVIGATION_EVENTS } from '@/lib/events/constants';
import { addEventListener, emitEvent } from '@/lib/events/helpers';
import type {
  ForbiddenApiErrorEventDetail,
  GlobalErrorEventDetail,
} from '@/lib/events/types';

/**
 * エラーダイアログのイベントリスナーを初期化
 * アプリケーション起動時に一度だけ呼び出される
 */
export const initializeErrorDialogEventListeners = () => {
  // GlobalErrorDialogイベントリスナー
  const handleGlobalErrorEvent = (
    event: CustomEvent<GlobalErrorEventDetail>,
  ) => {
    // 動的インポートでダイアログアクションを取得
    import('@/components/Error/GlobalErrorDialog').then(
      ({ openGlobalErrorDialog, closeGlobalErrorDialog }) => {
        const { detail } = event;

        openGlobalErrorDialog({
          title: detail.title,
          description: detail.description,
          actionType: detail.actionType,
          onGoBack: () => {
            // まずダイアログを閉じる
            closeGlobalErrorDialog();

            // isHistoryBackIfPossibleフラグが明示的にtrueの場合のみブラウザバック処理を実行
            if (detail.isHistoryBackIfPossible === true) {
              // navigation:historyBack イベントを発行してブラウザバック処理を実行
              emitEvent(NAVIGATION_EVENTS.HISTORY_BACK, {
                fallbackUrl: '/',
              });
            }
            // isHistoryBackIfPossibleがfalseまたは未設定の場合は、ダイアログを閉じるだけ
          },
        });
      },
    );
  };

  // ForbiddenApiErrorDialogイベントリスナー
  const handleForbiddenApiErrorEvent = (
    event: CustomEvent<ForbiddenApiErrorEventDetail>,
  ) => {
    import('@/components/Error/ForbiddenApiErrorDialog').then(
      ({ openForbiddenApiErrorDialog, closeForbiddenApiErrorDialog }) => {
        const { detail } = event;

        openForbiddenApiErrorDialog({
          accessibles: detail.accessibles,
          onGoBack: () => {
            // ダイアログを閉じる
            closeForbiddenApiErrorDialog();
          },
        });
      },
    );
  };

  // イベントリスナーを登録（エラーダイアログ専用）
  addEventListener(ERROR_EVENTS.GLOBAL, handleGlobalErrorEvent);
  addEventListener(ERROR_EVENTS.FORBIDDEN_API, handleForbiddenApiErrorEvent);
};
